import React, { useEffect, useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  Link,
} from "@mui/material";
import { useLocation } from "react-router-dom";

import Comment from "../Comment";
import Content from "../Content";
import ImageSlider from "../ImageSlider";
import styles from "./index.module.css";
import PricingTable from "../../PricingTable";
import { isCustomerFacingPriceRow, isCustomerFacingVisibleLine } from "../../../utils/totals";
import { resolveAdditionalCostFlags } from "../../../utils/pricingEngine";

const IMG_EXT_RX = /\.(png|jpe?g|gif|webp|bmp|svg)(?:$|[?#])/i;
const resolveArtworkDisplayUrl = (aw = {}) => (
  aw?.previewUrl ||
  aw?.PreviewUrl ||
  aw?.frontendImage ||
  aw?.frontendUrl ||
  aw?.Image ||
  null
);
const resolveArtworkOpenUrl = (aw = {}) => aw?.Image || aw?.FileUrl || resolveArtworkDisplayUrl(aw);
const getFileExt = (url = '') => {
  const m = String(url).match(/\.([a-z0-9]+)(?:\?|#|$)/i);
  return m ? m[1].toUpperCase() : 'FILE';
};
const toCents = (n) => Math.round((Number(n) || 0) * 100);
const fromCents = (c) => (Number(c) || 0) / 100;
const allocateByQuantityExact = (quantities = [], totalCents = 0) => {
  const safeTotal = Math.max(0, Number(totalCents) || 0);
  const q = quantities.map((n) => Math.max(0, Number(n) || 0));
  const totalUnits = q.reduce((sum, n) => sum + n, 0);
  if (!safeTotal || !totalUnits) return q.map(() => 0);

  const basePerUnit = Math.floor(safeTotal / totalUnits);
  let remainder = safeTotal - (basePerUnit * totalUnits);
  const allocated = q.map((qty) => qty * basePerUnit);

  for (let i = 0; i < q.length && remainder > 0; i += 1) {
    const extra = Math.min(remainder, q[i]);
    allocated[i] += extra;
    remainder -= extra;
  }
  return allocated;
};

const getTaxBreakdown = (source) => {
  const rows = source?.taxBreakdown ?? source?.totals?.taxBreakdown;
  return Array.isArray(rows) ? rows.filter(Boolean) : [];
};

const getTaxLabel = (row = {}) => {
  const taxId = row?.taxId ?? row?.id;
  return row?.name || row?.type || row?.label || (taxId != null ? `Tax ${taxId}` : "Tax");
};

const formatTaxRate = (row = {}) => {
  const raw = Number(row?.rate);
  if (!Number.isFinite(raw) || raw <= 0) return "";
  const pct = raw > 1 ? raw : raw * 100;
  return `${Number(pct.toFixed(2)).toString()}%`;
};

const getTaxAmount = (row = {}) => {
  const amount = Number(row?.taxAmount ?? row?.amount ?? row?.value);
  return Number.isFinite(amount) ? amount : null;
};

const ProductDetails = ({
  productData,
  currency,
  estimationId,
  handleComments,
  salesOrderStatus,
  canComment,
  showPricing, // ← from parent
  showProductTotals = false,
  showArtwork, // ← from parent (only controls artwork UI visibility)
}) => {
  const {
    Images,
    ImageUrl,
    Imprinting,
    Name,
    Description,
    cartDetails,
    decorations,
    productAdditionalCharges,
    artworks: ArtWorks,
    taxRate: productTaxRate,
  } = productData || {};

  const sizes =
    productData?.displaySizes || Imprinting?.Sizes?.Values?.map((i) => i?.Name).filter(Boolean) || [];
  const colors =
    productData?.displayColors || Imprinting?.Colors?.Values?.map((i) => i?.Name).filter(Boolean) || [];

  const totalQuantity = (cartDetails || []).reduce(
    (acc, item) => acc + (Number(item?.Quantity) || 0),
    0
  );

  const embeddedIncludeByRow = useMemo(() => {
    const rows = Array.isArray(cartDetails) ? cartDetails : [];
    const rowQuantities = rows.map((item) => Number(item?.Quantity) || 0);
    const fallbackQty = rowQuantities.reduce((sum, qty) => sum + qty, 0);
    if (!fallbackQty) return rowQuantities.map(() => 0);

    const processedIncludeKeys = new Set();
    const includeTotalCents = (decorations || []).reduce((sum, decoItem) => {
      const charges = decoItem?.Charge || [];
      return sum + charges.reduce((acc, ch) => {
        const flags = resolveAdditionalCostFlags(ch);
        if (!flags.includeInPrice) return acc;
        const sharedKey = String(ch?.sharedGroupKey ?? "").trim();
        if (sharedKey) {
          const dedupeKey = `${sharedKey}|${flags.costType}|${ch?.chargeId ?? ch?.Name ?? ""}`;
          if (processedIncludeKeys.has(dedupeKey)) return acc;
          processedIncludeKeys.add(dedupeKey);
        }
        const retailC = toCents(ch?.Retail ?? ch?.retail);
        const qtyRaw = Number(ch?.Quantity ?? ch?.quantity);
        const multiplier = flags.costType === "fixed"
          ? 1
          : (Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : fallbackQty);
        return acc + (retailC * multiplier);
      }, 0);
    }, 0);

    const productIncludeTotalCents = (productAdditionalCharges || []).reduce((sum, ch) => {
      const flags = resolveAdditionalCostFlags(ch);
      if (!flags.includeInPrice) return sum;
      const retailC = toCents(ch?.Retail ?? ch?.retail);
      const qtyRaw = Number(ch?.Quantity ?? ch?.quantity);
      const multiplier = flags.costType === "fixed" ? 1 : (Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : fallbackQty);
      return sum + (retailC * multiplier);
    }, 0);

    return allocateByQuantityExact(rowQuantities, includeTotalCents + productIncludeTotalCents);
  }, [cartDetails, decorations, productAdditionalCharges]);

  const displayCartRows = useMemo(() => {
    const rows = Array.isArray(cartDetails) ? cartDetails : [];
    return rows.map((item, idx) => {
      const quantity = Number(item?.Quantity) || 0;
      const basePriceC = toCents(item?.Price);
      const embeddedC = Number(embeddedIncludeByRow?.[idx]) || 0;
      const totalC = (basePriceC * quantity) + embeddedC;
      const perUnit = quantity > 0 ? fromCents(totalC / quantity) : fromCents(basePriceC);
      return {
        item,
        quantity,
        price: Number(perUnit.toFixed(2)),
        amount: fromCents(totalC),
      };
    });
  }, [cartDetails, embeddedIncludeByRow]);

  // ---------- Price rows for the table ----------
  const priceDetails = () => {
    const rows = displayCartRows.map(({ item, price, quantity, amount }) => {
      const label =
        [item?.size, item?.color].filter(Boolean).join(" - ") || "Item";
      return {
        item: label,
        price,
        quantity,
        amount: parseFloat(Number(amount || 0).toFixed(2)),
      };
    });

    const extraRows = (productAdditionalCharges || [])
      .filter((ch) => isCustomerFacingVisibleLine(ch))
      .map((ch) => {
        const flags = resolveAdditionalCostFlags(ch);
        const qtyRaw = Number(ch?.Quantity ?? ch?.quantity);
        const quantity = flags.costType === "fixed" ? 1 : (Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 0);
        const price = Number(ch?.Retail ?? ch?.retail) || 0;
        return { item: ch?.Name || ch?.CostName || "Additional Cost", price, quantity, amount: +(price * quantity).toFixed(2) };
      });

    return [
      ...rows,
      ...extraRows,
      { item: "Total Units", price: "", quantity: totalQuantity, amount: "" },
    ];
  };

  const quantityOnlyDetails = () => {
    const rows = (cartDetails || []).map((item) => {
      const label =
        [item?.size, item?.color].filter(Boolean).join(" - ") || "Item";
      const quantity = Number(item?.Quantity) || 0;
      return { item: label, price: "", quantity, amount: "" };
    });
    const extraRows = (productAdditionalCharges || [])
      .filter((ch) => isCustomerFacingVisibleLine(ch))
      .map((ch) => {
        const flags = resolveAdditionalCostFlags(ch);
        const qtyRaw = Number(ch?.Quantity ?? ch?.quantity);
        const quantity = flags.costType === "fixed" ? 1 : (Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 0);
        const price = Number(ch?.Retail ?? ch?.retail) || 0;
        return { item: ch?.Name || ch?.CostName || "Additional Cost", price, quantity, amount: +(price * quantity).toFixed(2) };
      });

    return [
      ...rows,
      ...extraRows,
      { item: "Total Units", price: "", quantity: totalQuantity, amount: "" },
    ];
  };

  // ---------- Robust tax handling ----------
  // Pull tax rate from product, or fallback to a per-line field if present.
  const sniffItemRate = useMemo(() => {
    const items = cartDetails || [];
    for (const it of items) {
      const r =
        Number(it?.taxRate?.rate) ||
        Number(it?.TaxRate?.rate) ||
        Number(it?.TaxRate) ||
        Number(it?.taxRate);
      if (Number.isFinite(r) && r > 0) return r;
    }
    return 0;
  }, [cartDetails]);

  const explicitRate = Number(productTaxRate?.rate);
  const effectiveRate =
    Number.isFinite(explicitRate) && explicitRate > 0
      ? explicitRate
      : sniffItemRate > 0
      ? sniffItemRate
      : 0;

  const hasTax = effectiveRate > 0;

  // Assume line prices include tax if explicitly marked
  const assumeIncluded =
    Boolean(productData?.pricesIncludeTax) ||
    (cartDetails || []).some((i) => i?.TaxIncluded === true);

  // Build nominal totals from lines (whatever Price/Retail represent)
  const unitRaw = (cartDetails || []).reduce(
    (s, _, idx) => s + (Number(displayCartRows?.[idx]?.amount) || 0),
    0
  );

  const getArtworkForDecoration = (decoration, idx) => {
    const decorationId = decoration?.id != null ? String(decoration.id) : null;
    if (decorationId) {
      const byId = (ArtWorks || []).find((aw) => String(aw?.DecorationId ?? "") === decorationId);
      if (byId) return byId;
    }
    return (ArtWorks || [])[idx] || null;
  };

  const productExtraRaw = (productAdditionalCharges || []).reduce((sum, ch) => {
    if (!isCustomerFacingVisibleLine(ch)) return sum;
    const retail = Number(ch?.Retail ?? ch?.retail) || 0;
    const flags = resolveAdditionalCostFlags(ch);
    const qtyRaw = Number(ch?.Quantity ?? ch?.quantity);
    const multiplier = flags.costType === "fixed" ? 1 : (Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 0);
    return sum + (retail * multiplier);
  }, 0);

  const decoRaw = (decorations || []).reduce((sum, decoItem) => {
    const charges = decoItem?.Charge || [];
    const itemTotal = charges.reduce((acc, ch) => {
      if (!isCustomerFacingPriceRow(ch)) return acc;
      const retail = Number(ch?.Retail) || 0;
      const qtyRaw = Number(ch?.Quantity ?? ch?.quantity);
      const flags = resolveAdditionalCostFlags(ch);
      const multiplier = flags.costType === "fixed"
        ? 1
        : (Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 0);
      return acc + retail * multiplier;
    }, 0);
    return sum + itemTotal;
  }, 0);

  const artworkRaw = (decorations || []).reduce((sum, decoration, idx) => {
    const aw = getArtworkForDecoration(decoration, idx);
    const artCharge = Number(aw?.Charge) || 0;
    if (artCharge <= 0) return sum;
    return sum + (artCharge * totalQuantity);
  }, 0);
  const includeDeco = decoRaw > 0 || artworkRaw > 0;
  const rawSum = unitRaw + (includeDeco ? decoRaw + artworkRaw : 0) + productExtraRaw;

  // Normalize to pre-tax, then compute tax & total
  const preTax = hasTax && assumeIncluded ? rawSum / (1 + effectiveRate) : rawSum;
  const tax = hasTax ? preTax * effectiveRate : 0;
  const totalIncl = preTax + tax;



const taxBreakdown = getTaxBreakdown(productData);
const hasBreakdown = taxBreakdown.length > 0;
const isMultiBreakdown = taxBreakdown.length > 1;
const payloadPreTax = Number(productData?.totals?.preTax);
const aggregateTaxFromPayload = Number(productData?.taxAmount ?? productData?.totals?.tax);
const payloadTotalIncl = Number(
  productData?.totalInclTax ??
  productData?.totalIncl ??
  productData?.totals?.totalIncl
);

const computedPreTaxCents = toCents(preTax);
const payloadPreTaxCents = Number.isFinite(payloadPreTax)
  ? toCents(payloadPreTax)
  : null;
const hasRenderedSubtotal = computedPreTaxCents > 0 || rawSum > 0;
const hasAuthoritativeTaxAmount = Number.isFinite(aggregateTaxFromPayload);
const hasAuthoritativeBreakdown = taxBreakdown.some((row) => (
  getTaxAmount(row) != null ||
  Number.isFinite(Number(row?.taxableAmount))
));
const hasAuthoritativeTax = hasAuthoritativeTaxAmount || hasAuthoritativeBreakdown;

const displayPreTaxCents =
  hasRenderedSubtotal || payloadPreTaxCents == null
    ? computedPreTaxCents
    : payloadPreTaxCents;

const displayBreakdownRows = taxBreakdown.map((row) => {
  const rate = Number(row?.rate) || 0;
  const resolvedAmount = getTaxAmount(row);
  const taxableAmount = Number(row?.taxableAmount);

  return {
    ...row,
    displayAmountCents:
      resolvedAmount != null
        ? toCents(resolvedAmount)
        : Number.isFinite(taxableAmount)
          ? Math.round(toCents(taxableAmount) * rate)
          : Math.round(displayPreTaxCents * rate),
  };
});

const breakdownTaxTotalCents =
  hasBreakdown && hasAuthoritativeTaxAmount
    ? toCents(aggregateTaxFromPayload)
    : displayBreakdownRows.reduce((sum, row) => sum + (row.displayAmountCents || 0), 0);

const displayTaxCents = hasBreakdown
  ? (hasAuthoritativeTax ? breakdownTaxTotalCents : displayBreakdownRows.reduce((sum, row) => sum + (row.displayAmountCents || 0), 0))
  : (hasAuthoritativeTaxAmount
    ? toCents(aggregateTaxFromPayload)
    : (hasTax ? Math.round(displayPreTaxCents * effectiveRate) : 0));

const displayTotalInclCents =
  hasAuthoritativeTax &&
  Number.isFinite(payloadTotalIncl)
    ? toCents(payloadTotalIncl)
    : displayPreTaxCents + displayTaxCents;

const centsDisplayPreTax = fromCents(displayPreTaxCents);
const centsDisplayTax = fromCents(displayTaxCents);
const centsDisplayTotalIncl = fromCents(displayTotalInclCents);



  // ---- DEBUG switch (?debug=totals) ----
  const { search: rrSearch } = useLocation();
  const getSearchString = () => {
    if (rrSearch) return rrSearch;
    const hash = typeof window !== "undefined" ? window.location.hash || "" : "";
    const idx = hash.indexOf("?");
    return idx >= 0 ? hash.slice(idx) : "";
  };
  const rawDebug = (() => {
    const v = new URLSearchParams(getSearchString()).get("debug");
    return v === null ? "true" : v;
  })();
  const debugTotals =
    !!rawDebug &&
    ["", "1", "true", "y", "yes", "totals", "all"].includes(
      String(rawDebug).trim().toLowerCase()
    );

  if (debugTotals) {
    // eslint-disable-next-line no-console
    console.groupCollapsed("Presentation Totals Debug");
    // eslint-disable-next-line no-console
    console.log({
      hasTax,
      effectiveRate,
      assumeIncluded,
      includeDeco,
      unitRaw,
      decoRaw,
      artworkRaw,
      rawSum,
      preTax,
      tax,
      totalIncl,
    });
    // eslint-disable-next-line no-console
    console.groupEnd();
  }

  // Map decorationId → location
  const locationByDecorationId = new Map(
    (decorations || []).map((d) => [String(d?.id), d?.Location])
  );

  const buildDisplayFileUrl = (aw) => {
    const display = resolveArtworkDisplayUrl(aw);
    if (display) return display;
    if (aw?.CustomerFileId) return `/api/customer-files/${aw.CustomerFileId}`;
    return null;
  };

  const buildOpenFileUrl = (aw) => {
    const open = resolveArtworkOpenUrl(aw);
    if (open) return open;
    if (aw?.CustomerFileId) return `/api/customer-files/${aw.CustomerFileId}`;
    return null;
  };

  // Artwork cards (UI only)
  let artworkDisplay = (ArtWorks || []).map((aw) => {
    const decId = aw?.DecorationId != null ? String(aw.DecorationId) : null;
    const location = (decId && locationByDecorationId.get(decId)) || "";
    const fileUrl = buildDisplayFileUrl(aw);
    const openUrl = buildOpenFileUrl(aw);
    const fileName = aw?.LogoName || "Artwork file";

    return {
      id: aw?.id || `${aw?.DecorationId || ""}-${aw?.LogoName || ""}`,
      fileUrl,
      openUrl,
      fileName,
      imprintType: aw?.ImprintType || "",
      location,
      colors: aw?.LogoColor || aw?.LogoColors || null,
      notes: aw?.SupplierNotes || null,
      charge: Number(aw?.Charge) || 0,
    };
  });

  if (!artworkDisplay.length && (decorations || []).length) {
    artworkDisplay = (decorations || []).map((d) => ({
      id: `dec-${d?.id}`,
      fileUrl: null,
      fileName: "Decoration",
      imprintType: "",
      location: d?.Location || "",
      colors: null,
      notes: null,
      charge: 0,
    }));
  }

  const showComments = Boolean(canComment);

  return (
    <Grid container spacing={3} className={styles.presentationCardContainer}>
      <Grid style={{ padding: "12px" }}>
        <Content title={Name} description={Description} type={"Text"} />
      </Grid>

      <Grid container spacing={3} className={styles.presentationCard}>
        <Grid
          item
          xs={12}
          md={4}
          className={styles.ImageContainer}
          style={{ padding: "12px" }}
        >
          <ImageSlider productImage={Images} mainImage={ImageUrl} useImageCDN={false} />
        </Grid>

        <Grid item xs={12} md={7} style={{ paddingLeft: "0px", paddingTop: "0px" }}>
          <Box>
            {!!colors.length && (
              <Content title={"Color"} description={colors.join(", ")} type={"Text"} />
            )}

            {!!sizes.length && (
              <Content title={"Sizes"} description={sizes.join(", ")} type={"Text"} />
            )}

            {/* Unit pricing visible only when showPricing */}
            {showPricing && !!(cartDetails || []).length && (
              <PricingTable priceDetails={priceDetails()} />
            )}

            {/* Quantity table when pricing is hidden */}
            {!showPricing && !!(cartDetails || []).length && (
              <PricingTable
                priceDetails={quantityOnlyDetails()}
                showPleaseInquireForEmptyPrice
              />
            )}

            {/* Decoration charge tables only when BOTH pricing and artwork are visible */}
            {showPricing && showArtwork && (decorations || []).length > 0 && (
              <Grid
                item
                xs={12}
                md={7}
                style={{ paddingLeft: "0px", paddingTop: "0px", minWidth: "100%" }}
              >
                {(decorations || []).map((decoration, idx) => {
                  const chargesTable = (decoration?.Charge || [])
                    .filter((item) => isCustomerFacingVisibleLine(item))
                    .map((item) => ({
                      decoration: decoration?.Location,
                      item: item?.Name,
                      price: item?.Retail,
                      quantity: item?.Quantity,
                      amount: item?.Total,
                    }));

                  const aw = getArtworkForDecoration(decoration, idx);
                  const perUnitArtCharge = Number(aw?.Charge) || 0;
                  if (perUnitArtCharge > 0) {
                    chargesTable.push({
                      decoration: "",
                      item: `Imprint Cost - ${aw?.ImprintType || ""}`,
                      price: perUnitArtCharge,
                      quantity: totalQuantity,
                      amount: perUnitArtCharge * totalQuantity,
                    });
                  }

                  if (!chargesTable.length) return null;

                  return (
                    <PricingTable
                      key={idx}
                      priceDetails={chargesTable}
                      isPo={true}
                      isArtWork={true}
                    />
                  );
                })}
              </Grid>
            )}

            {/* Artwork details */}
            {showArtwork && artworkDisplay.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Artwork
                </Typography>
                <Box sx={{ display: "grid", gap: 1.5 }}>
                  {artworkDisplay.map((aw) => (
                    <Box
                      key={aw.id}
                      sx={{ border: "1px solid #e5e7eb", borderRadius: 1, p: 1.25 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        {aw.fileUrl && (
                          <Box
                            sx={{
                              width: 72,
                              height: 72,
                              borderRadius: 1,
                              overflow: "hidden",
                              flexShrink: 0,
                              border: '1px solid #e5e7eb',
                              display: 'grid',
                              placeItems: 'center',
                              bgcolor: '#fafafa',
                            }}
                          >
                            {IMG_EXT_RX.test(String(aw.fileUrl || '')) ? (
                              <img
                                src={aw.fileUrl}
                                alt={aw.fileName}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                }}
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                            ) : (
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {getFileExt(aw.openUrl || aw.fileUrl)}
                              </Typography>
                            )}
                          </Box>
                        )}

                        <Box sx={{ flex: 1, minWidth: 220 }}>
                          <Typography variant="body2">
                            <b>Location:</b> {aw.location || "—"}
                          </Typography>
                          <Typography variant="body2">
                            <b>Imprint:</b> {aw.imprintType || "—"}
                          </Typography>
                          {aw.colors && (
                            <Typography variant="body2">
                              <b>Colors:</b>{" "}
                              {Array.isArray(aw.colors)
                                ? aw.colors.join(", ")
                                : aw.colors}
                            </Typography>
                          )}
                          {aw.notes && (
                            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                              <b>Notes:</b>{"\n"}{aw.notes}
                            </Typography>
                          )}

                          <Typography variant="body2">
                            <b>Quantity:</b> {totalQuantity}
                          </Typography>

                          {showPricing && Number.isFinite(aw.charge) && aw.charge > 0 && (
                            <Typography variant="body2">
                              <b>Artwork Charge:</b> {aw.charge} {currency}
                            </Typography>
                          )}
                        </Box>

                        {aw.fileUrl && (
                          <Box sx={{ ml: "auto" }}>
                            <Link
                              href={aw.openUrl || aw.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                            >
                              {aw.fileName}
                            </Link>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Comments */}
            <Comment
              handleComments={handleComments}
              productData={productData}
              presentationId={estimationId}
            />

            {/* Totals footer — only the three lines */}
            {showPricing && showProductTotals && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  borderTop: "1px solid #ccc",
                  mt: 2,
                  pt: 3,
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box sx={{ visibility: "hidden" }}>.</Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography component="div">
                    Subtotal (before tax): {centsDisplayPreTax.toFixed(2)} {currency}
                  </Typography>
                  {hasBreakdown ? (
                    <>
                      {displayBreakdownRows.map((row, idx) => {
                        const label = getTaxLabel(row);
                        const rateText = formatTaxRate(row);
                        const amount = fromCents(row.displayAmountCents);
                        return (
                          <Typography key={`${label}-${idx}`} component="div" variant="body2">
                            {label}{rateText ? ` (${rateText})` : ""}: {amount.toFixed(2)} {currency}
                          </Typography>
                        );
                      })}
                      {isMultiBreakdown ? (
                        <Typography component="div" variant="body2">
                          Tax Total: {centsDisplayTax.toFixed(2)} {currency}
                        </Typography>
                      ) : null}
                    </>
                  ) : (hasTax || hasAuthoritativeTaxAmount) ? (
                    <Typography component="div" variant="body2">
                      Tax{hasTax ? ` (${(effectiveRate * 100).toFixed(2)}%)` : ""}: {centsDisplayTax.toFixed(2)} {currency}
                    </Typography>
                  ) : null}
                  <Typography component="div" sx={{ fontWeight: 700 }}>
                    Total (incl. tax): {centsDisplayTotalIncl.toFixed(2)} {currency}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ProductDetails;
